import { Alert, Button, Card, Descriptions, Skeleton, Space, Table, Typography } from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate, useParams } from 'react-router-dom';
import type { TariffComponent, TouPeriod } from '@goelectric/shared-types';
import { useAuth } from '@/features/auth/context/AuthContext';
import { canWriteTarifas } from '@/layouts/navigation';
import { useTariff } from '../hooks/useTariff';
import { TariffOcpiBadge, TariffTypeBadge } from '../components/TariffBadges';
import {
  formatComponentPrice,
  formatDateTime,
  formatVigencia,
  TARIFF_COMPONENT_LABELS,
} from '../utils/format';

/** Clave estable del componente: su contenido (no depende del índice de la fila). */
function componentRowKey(component: TariffComponent): string {
  return JSON.stringify(component);
}

/** Clave estable del periodo TOU: etiqueta, franja y precio. */
function periodRowKey(period: TouPeriod): string {
  return `${period.label}-${period.startTime}-${period.endTime}-${period.priceCopPerKwh}`;
}

const componentColumns: ColumnsType<TariffComponent> = [
  {
    title: 'Componente',
    dataIndex: 'type',
    key: 'type',
    render: (type: TariffComponent['type']) => TARIFF_COMPONENT_LABELS[type],
  },
  {
    title: 'Precio',
    key: 'priceCop',
    align: 'right',
    render: (_, component) => formatComponentPrice(component.priceCop, component.type),
  },
  {
    title: 'IVA',
    dataIndex: 'vatRatePct',
    key: 'vatRatePct',
    align: 'right',
    render: (value: number) => `${value} %`,
  },
  {
    title: 'Paso',
    dataIndex: 'stepSize',
    key: 'stepSize',
    align: 'right',
    render: (value: number | undefined) => (value === undefined ? '—' : value),
  },
  {
    title: 'Descripción',
    dataIndex: 'description',
    key: 'description',
    render: (value: string | undefined) => value || '—',
  },
];

const periodColumns: ColumnsType<TouPeriod> = [
  { title: 'Periodo', dataIndex: 'label', key: 'label' },
  {
    title: 'Franja',
    key: 'range',
    render: (_, period) => `${period.startTime} – ${period.endTime}`,
  },
  {
    title: 'Precio',
    key: 'price',
    align: 'right',
    render: (_, period) => formatComponentPrice(period.priceCopPerKwh, 'ENERGY'),
  },
];

/** Detalle de solo lectura de una Tarifa (`/tarifas/:tariffId`). */
export function TariffDetailPage() {
  const { tariffId } = useParams<{ tariffId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tariff, loading, error, notFound, refresh } = useTariff(tariffId);
  const canWrite = canWriteTarifas(user?.role);

  const goToList = () => navigate('/tarifas');

  return (
    <div className="tarifas-page">
      <div className="tarifas-page__header">
        <Space align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al listado de tarifas"
            onClick={goToList}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            {tariff?.name ?? 'Detalle de la tarifa'}
          </Typography.Title>
        </Space>
        {canWrite && tariff && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/tarifas/${encodeURIComponent(tariff.id)}/editar`)}
          >
            Editar tarifa
          </Button>
        )}
      </div>

      {loading ? (
        <Card>
          <Skeleton active paragraph={{ rows: 5 }} />
        </Card>
      ) : notFound ? (
        <Alert
          type="warning"
          showIcon
          message="No encontramos la tarifa"
          description="Puede que se haya eliminado o que el enlace esté incompleto."
          action={
            <Button size="small" onClick={goToList}>
              Volver al listado
            </Button>
          }
        />
      ) : error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar la tarifa"
          description={error}
          action={
            <Button size="small" onClick={refresh}>
              Reintentar
            </Button>
          }
        />
      ) : tariff ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Card>
            <Descriptions column={{ xs: 1, sm: 2 }} size="middle">
              <Descriptions.Item label="Identificador">
                <Typography.Text style={{ fontFamily: 'var(--ge-font-mono)' }}>
                  {tariff.id}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Tipo">
                <TariffTypeBadge type={tariff.type} />
              </Descriptions.Item>
              <Descriptions.Item label="Vigencia">
                {formatVigencia(tariff.validFrom, tariff.validTo)}
              </Descriptions.Item>
              <Descriptions.Item label="Moneda">{tariff.currency}</Descriptions.Item>
              <Descriptions.Item label="Marca OCPI">
                <TariffOcpiBadge published={tariff.publishedToOcpi ?? true} />
              </Descriptions.Item>
              <Descriptions.Item label="Actualizada">
                {formatDateTime(tariff.updatedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Descripción" span={2}>
                {tariff.description || '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Componentes de costo">
            <Table<TariffComponent>
              rowKey={componentRowKey}
              size="small"
              pagination={false}
              columns={componentColumns}
              dataSource={tariff.components}
            />
          </Card>

          {tariff.type === 'TOU' && (tariff.touPeriods?.length ?? 0) > 0 && (
            <Card title="Periodos TOU">
              <Table<TouPeriod>
                rowKey={periodRowKey}
                size="small"
                pagination={false}
                columns={periodColumns}
                dataSource={tariff.touPeriods}
              />
            </Card>
          )}
        </Space>
      ) : null}
    </div>
  );
}
